import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Alert } from '../components/ui/Alert';
import { landingPath } from '../utils/isFieldTech';

// IDI-815: /claim's symbol class (AccountClaim.js), verbatim.
const SPECIAL_CHAR = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/;

export default function Login() {
  const navigate = useNavigate();
  const { login, completeNewPassword, isAuthenticated, loading: authLoading, user } = useAuth();
  // IDI-582 S1: a field tech lands on Jobs, everyone else on the dashboard.
  // The post-await navigate routes on the user login() returns (set by
  // checkAuth before it resolves); the ref is only a fallback for callers
  // that don't return one.
  const userRef = useRef(user);
  userRef.current = user;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // IDI-121: one clear message when the session could not be renewed. Read
  // and cleared on first render so a refresh of /login doesn't repeat it.
  const [expiredNotice] = useState(() => {
    try {
      const r = sessionStorage.getItem('bidit:signedOutReason');
      sessionStorage.removeItem('bidit:signedOutReason');
      return r === 'expired';
    } catch { return false; }
  });

  // PT-48: NEW_PASSWORD_REQUIRED challenge state
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    // IDI-582: wait for the user too, so a tech is never routed on a null
    // user (landingPath(null) would be /dashboard).
    if (isAuthenticated && user) {
      navigate(landingPath(user), { replace: true });
    }
  }, [isAuthenticated, navigate, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Email and password are required'); return; }
    setLoading(true);
    setError('');

    const result = await login(email, password);
    if (result.success) {
      navigate(landingPath(result.user || userRef.current), { replace: true });
    } else if (result.challenge === 'NEW_PASSWORD_REQUIRED') {
      // User must set a new password on first login
      setNeedsNewPassword(true);
      setError('');
    } else {
      setError(result.message || 'Login failed');
    }
    setLoading(false);
  };

  const handleSetNewPassword = async (e) => {
    e.preventDefault();
    // IDI-815 (D3): /claim's rules, in /claim's order and words
    // (AccountClaim.js), checked before anything is sent.
    if (newPassword.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (!/[A-Z]/.test(newPassword)) { setError('Password must contain an uppercase letter'); return; }
    if (!/[a-z]/.test(newPassword)) { setError('Password must contain a lowercase letter'); return; }
    if (!/[0-9]/.test(newPassword)) { setError('Password must contain a number'); return; }
    if (!SPECIAL_CHAR.test(newPassword)) { setError('Password must contain a special character'); return; }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    setError('');

    const result = await completeNewPassword(newPassword);
    if (result.success) {
      navigate(landingPath(result.user || userRef.current), { replace: true });
    } else {
      setError(result.message || 'Failed to set new password');
    }
    setLoading(false);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-2xl mb-4">
            <span className="text-white text-2xl font-bold">B</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">BIDIT</h1>
          <p className="text-gray-500 mt-1">Insulation Estimating & Management</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl border border-gray-100 p-8">
          {expiredNotice && !error && !needsNewPassword && (
            <div role="status" data-testid="login-session-expired" className="mb-4">
              <Alert variant="warning">
                Your session expired, so you were signed out. Sign in again to continue; anything you saved is kept.
              </Alert>
            </div>
          )}

          {needsNewPassword ? (
            /* PT-48: Set New Password form (first login after invite) */
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Set your password</h2>
              <p className="text-sm text-gray-500 mb-6">Your account requires a new password. Choose a password you'll remember.</p>

              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  {error}
                </div>
              )}

              <form onSubmit={handleSetNewPassword} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">New password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-[color:var(--color-border)] focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm"
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    autoFocus
                  />
                  <div className="mt-2 text-xs text-gray-400 space-y-0.5" data-testid="login-password-requirements">
                    <p className="font-medium text-gray-500">Password requirements:</p>
                    <ul className="list-disc pl-4">
                      <li className={newPassword.length >= 8 ? 'text-green-600' : ''}>At least 8 characters</li>
                      <li className={/[A-Z]/.test(newPassword) ? 'text-green-600' : ''}>Uppercase letter</li>
                      <li className={/[a-z]/.test(newPassword) ? 'text-green-600' : ''}>Lowercase letter</li>
                      <li className={/[0-9]/.test(newPassword) ? 'text-green-600' : ''}>Number</li>
                      <li className={SPECIAL_CHAR.test(newPassword) ? 'text-green-600' : ''}>Special character (!@#$%^&*)</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-[color:var(--color-border)] focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm"
                    placeholder="Re-enter your password"
                    autoComplete="new-password"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white font-medium rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Setting password...
                    </>
                  ) : (
                    'Set password & sign in'
                  )}
                </button>
              </form>
            </>
          ) : (
            /* Standard login form */
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Sign in to your account</h2>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2">
              <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Email address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-[color:var(--color-border)] focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm"
                placeholder="you@company.com"
                autoComplete="email"
                autoFocus
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-gray-700">Password</label>
                <a href="/reset-password" className="text-xs text-primary-600 hover:text-primary-800">Forgot password?</a>
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-[color:var(--color-border)] focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white font-medium rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Signing in...
                </>
              ) : (
                'Sign in'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-400">
              Have an invite code? <a href="/claim" className="text-primary-600 hover:text-primary-800 font-medium">Claim your account</a>
            </p>
          </div>
            </>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-gray-400 mt-6">
          © {new Date().getFullYear()} BIDIT. All rights reserved.
        </p>
      </div>
    </div>
  );
}
